import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import debounce from 'bloko/common/debounce';

import { useUpdateDraftFilter } from 'src/components/Search/Common/Filters/hooks/draft/useUpdateDraftFilter';
import { useAreasDataProvider } from 'src/hooks/useAreasDataProvider';
import { useSelector } from 'src/hooks/useSelector';
import { CurrentGeoFilter, setSearchGeoDrawerCurrentFilter } from 'src/models/search/common/geoDrawer';
import { ClusterKey, ClusterGroup } from 'src/types/search/common/clusters';

import { useGeoFiltersAnalytics } from 'src/components/Search/Resume/Filters/Geo/hooks/useAnalytics';

const FILTER_DEBOUNCE = 300;
const SEARCH_QUERY_LIMIT_LOW = 2;

export const useSearchAreaState = () => {
    const [isAnalyticsSent, setAnalyticsSent] = useState(false);
    const { sendEventSearchAreas } = useGeoFiltersAnalytics();

    const [searchQuery, setSearchQuery] = useState('');
    const [searchItems, setSearchItems] = useState<Array<ClusterGroup>>([]);
    const currentGeoFilter = useSelector((state) => state.searchGeoDrawer.currentGeoFilter);
    const dispatch = useDispatch();

    const updateAreaDraftFilter = useUpdateDraftFilter(ClusterKey.Area);
    const updateDistrictDraftFilter = useUpdateDraftFilter(ClusterKey.District);

    const dataProvider = useAreasDataProvider(true);
    const debouncedSearch = useMemo(
        () =>
            debounce(async (query: string) => {
                const data = await dataProvider(query);
                const searchedItems = data.items.map(({ id, text }) => ({ id: String(id), title: text, count: 0 }));
                setSearchItems(searchedItems);
            }, FILTER_DEBOUNCE),
        [dataProvider]
    );
    const handleQueryChange = useCallback(
        (query: string) => {
            if (currentGeoFilter !== CurrentGeoFilter.AREA) {
                dispatch([setSearchGeoDrawerCurrentFilter(CurrentGeoFilter.AREA)]);
            }

            setSearchQuery(query);
            if (query.length >= SEARCH_QUERY_LIMIT_LOW) {
                debouncedSearch(query);
                if (!isAnalyticsSent) {
                    setAnalyticsSent(true);
                    sendEventSearchAreas();
                }
            } else {
                setSearchItems([]);
                if (isAnalyticsSent && query.length === 0) {
                    setAnalyticsSent(false);
                }
            }
        },
        [currentGeoFilter, dispatch, debouncedSearch, isAnalyticsSent, sendEventSearchAreas]
    );

    const handleClearButtonClick = useCallback(() => {
        if (currentGeoFilter !== CurrentGeoFilter.AREA) {
            dispatch([setSearchGeoDrawerCurrentFilter(CurrentGeoFilter.AREA)]);
            updateDistrictDraftFilter([]);
            updateAreaDraftFilter([]);
        }
    }, [currentGeoFilter, dispatch, updateAreaDraftFilter, updateDistrictDraftFilter]);

    return useMemo(
        () => ({
            query: searchQuery,
            showSearchItems: searchQuery.length > 0,
            searchItems,
            handleQueryChange,
            handleClearButtonClick,
        }),
        [searchQuery, searchItems, handleQueryChange, handleClearButtonClick]
    );
};
